import { useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
    AlertTriangle,
    Calendar,
    History,
} from "lucide-react";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { SHIFT_CHANGE_REQUEST } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { truncateText } from "../../components/common/CommonFunctions";
import { apiErrorMessage, canReviewShiftChangeRequests, requestTypeLabel, shiftRequestStatusLabel } from "./helpers/shiftChangeUtils";
import ShiftChangeReviewDialog, { SHIFT_CHANGE_STATUS_BADGE } from "./helpers/ShiftChangeReviewDialog";

const ShiftChangeRequests = () => {
    const { update } = useApiMethods();
    const isAdmin = JSON.parse(sessionStorage.getItem("is_admin") || "false");
    const isSuperAdmin = JSON.parse(sessionStorage.getItem("is_super_admin") || "false");
    const loggedInEmployeeDbId = Number(sessionStorage.getItem("employee_id") || 0);
    const canView = canReviewShiftChangeRequests();
    const canReviewOthers = isAdmin || isSuperAdmin;

    const isOwnRequest = useCallback((record) => {
        const recordDbId = Number(record?.employee_db_id ?? record?.employee_details?.id ?? 0);
        if (loggedInEmployeeDbId && recordDbId) {
            return loggedInEmployeeDbId === recordDbId;
        }
        return false;
    }, [loggedInEmployeeDbId]);

    const [rows, setRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [loading, setLoading] = useState(false);
    const [approveOpen, setApproveOpen] = useState(false);
    const [detailMode, setDetailMode] = useState("view");
    const [selected, setSelected] = useState(null);
    const [applicationScope, setApplicationScope] = useState("one_day");
    const [adminComment, setAdminComment] = useState("");

    const refresh = () => setServerTrigger((v) => !v);

    const openDetail = (row, mode = "view") => {
        setSelected(row);
        setDetailMode(mode);
        setApplicationScope("one_day");
        setAdminComment("");
        setApproveOpen(true);
    };

    const handleApprove = async (status) => {
        if (!selected?.id) return;
        if (status === "approved" && selected.request_type !== "swap" && !applicationScope) {
            ToastMsg("error", "Select how to apply the shift change");
            return;
        }
        try {
            setLoading(true);
            const payload = {
                id: selected.id,
                status,
                admin_comment: adminComment.trim(),
            };
            if (status === "approved" && selected.request_type !== "swap") {
                payload.application_scope = applicationScope;
            }
            const res = await update(SHIFT_CHANGE_REQUEST, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Updated");
                setApproveOpen(false);
                setSelected(null);
                refresh();
            } else {
                ToastMsg("error", apiErrorMessage(res, "Action failed"));
            }
        } catch (e) {
            ToastMsg("error", apiErrorMessage(e, "Action failed"));
        } finally {
            setLoading(false);
        }
    };

    const columns = useMemo(() => [
        { accessor: "employee_name", Header: "Employee", sortable: true, filterable: true },
        {
            accessor: "request_type",
            Header: "Type",
            Cell: ({ value }) => requestTypeLabel(value),
        },
        {
            accessor: "date",
            Header: "Date",
            sortable: true,
            Cell: ({ value }) => (value ? dayjs(value).format("DD MMM YYYY") : "-"),
        },
        { accessor: "current_shift_name", Header: "Current Shift" },
        { accessor: "requested_shift_name", Header: "Requested Shift" },
        {
            accessor: "counterpart_name",
            Header: "Swap with",
            Cell: ({ value, row }) => ((row?.original ?? row)?.request_type === "swap" ? (value || "—") : "—"),
        },
        {
            accessor: "late_minutes_at_request",
            Header: "Late (min)",
            align: "center",
            Cell: ({ value }) => value ?? 0,
        },
        {
            accessor: "status",
            Header: "Status",
            Cell: ({ row }) => {
                const record = row?.original ?? row;
                const label = shiftRequestStatusLabel(record);
                return (
                    <span className={cn("px-2 py-0.5 rounded-full text-xs border capitalize", SHIFT_CHANGE_STATUS_BADGE[record.status] || SHIFT_CHANGE_STATUS_BADGE.pending)}>
                        {label}
                    </span>
                );
            },
        },
        { accessor: "reason", Header: "Reason", Cell: ({ value }) => truncateText(value, 36) },
        {
            accessor: "admin_comment",
            Header: "Review note",
            Cell: ({ value }) => truncateText(value, 32, "240px"),
        },
        {
            Header: "Action",
            align: "right",
            fixed: "right",
            Cell: ({ row }) => {
                const record = row?.original ?? row;

                if (record.status === "pending" && canReviewOthers && !isOwnRequest(record) && record.can_review !== false) {
                    return (
                        <Button
                            size="sm"
                            className="bg-sidebar text-white hover:bg-sidebar-primary"
                            onClick={() => openDetail(record, "review")}
                        >
                            Review
                        </Button>
                    );
                }

                return (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="View request details"
                        onClick={() => openDetail(record, "view")}
                    >
                        <History className="h-4 w-4 text-sidebar-primary" />
                    </Button>
                );
            },
        },
    ], [canReviewOthers, isOwnRequest]);

    if (!canView) {
        return (
            <ContainerCard>
                <div className="flex flex-col items-center justify-center py-24 text-center">
                    <AlertTriangle className="h-10 w-10 text-amber-500 mb-3" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Access denied</h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-md">
                        This page is for reviewing team shift change and swap requests. To view your own requests, open
                        {" "}<strong>My Shift Change / Swap</strong> under Attendance.
                    </p>
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard>
            <div className="flex justify-between items-center mb-4">
                <ModuleTitleCard
                    Icon={Calendar}
                    title="Shift Change / Swap Approvals"
                    subtitle="Review change and swap requests from your team"
                    totalRecords={totalRecords}
                />
            </div>

            <Reactable
                columns={columns}
                data={rows}
                setData={setRows}
                setTotalCount={setTotalRecords}
                url={SHIFT_CHANGE_REQUEST}
                serverSide
                serverTrigger={serverTrigger}
                rowSize="sm"
                isCSVRequired={false}
                isPDFRequired={false}
            />

            <ShiftChangeReviewDialog
                open={approveOpen}
                onClose={() => setApproveOpen(false)}
                selected={selected}
                applicationScope={applicationScope}
                onScopeChange={setApplicationScope}
                adminComment={adminComment}
                onCommentChange={setAdminComment}
                loading={loading}
                onApprove={() => handleApprove("approved")}
                onReject={() => handleApprove("rejected")}
                readOnly={detailMode === "view"}
            />
        </ContainerCard>
    );
};

export default ShiftChangeRequests;
